import { useState, type CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import {
  GARMENT_CATEGORY_LABELS,
  HYPE_LIST,
  MOODS,
  MOOD_LIST,
  RACKSAVANT,
  ROOM_CODE_LENGTH,
  formatCount,
  formatPrice,
  type MoodId,
} from '@racksavant/core';
import { cx } from '../cx';
import { Reveal, Wipe } from '../components/Reveal';
import { Cutout, Face, Plate } from '../components/Photo';
import { Marquee } from '../components/Marquee';
import { DeviceDeck } from '../components/DeviceDeck';
import { ArrowRight } from '../components/icons';
import { useStats } from '../hooks/useStats';
import { mediaSrc, useGarments, useRunway } from '../hooks/useCatalogue';
import ui from '../ui.module.css';
import s from './OutsideLandsPage.module.css';

/* =========================================================================
   RackSavant × Outside Lands — the festival issue.

   This page has to do two things that pull in opposite directions. It is our
   entry in a hackathon for a festival whose whole visual language is loud,
   warm and psychedelic; and it is a page of our product, whose visual language
   is restrained editorial print. Abandoning ours for theirs would make this
   look like a different company.

   So it is resolved the way a magazine resolves it — this is the festival
   ISSUE, not a different magazine:

     · The TYPE is ours, unchanged. Display serif, negative leading, masthead
       scale, mono eyebrows tracked out to a crop mark, square corners,
       hairline rules, grain over everything.
     · The STRUCTURE is theirs. A lineup bill: one headliner set enormous,
       then descending tiers, then the small-type run along the bottom. Except
       the acts are the three surfaces of the product and the small type is the
       mood taxonomy, so every word on the bill is a true statement about what
       we built.
     · The COLOUR is theirs, supplied by us. Not one new hex is invented here.
       The festival palette IS the mood palette — sunlit, maximal, nostalgic,
       feral — which `tokens.ts` explicitly holds outside the brand system as
       content, and which is therefore allowed to be this loud. It arrives as
       print ink: riso overprints, halftone screens and misregistration on
       photographs, never as UI fill.
     · The GROUND flips. The hero is a poster physically printed on bone stock;
       the rest of the page is our ink. Paper is a surface we already own.

   Every number on this page is measured. `/v1/stats`, `/v1/garments?mood=…`
   and `/v1/feed?scope=runway` are read live, and a count that does not come
   back is not printed — the copy is written to be complete without it.

   On the festival's marks: the name appears as plain text and nothing else.
   No logo, no wordmark, no artwork, no artist. It is a hackathon entry and it
   says so, twice.
   ========================================================================= */

/** The four moods a weekend in a park actually runs through. */
const FESTIVAL_MOODS: MoodId[] = ['sunlit', 'maximal', 'nostalgic', 'feral'];

/**
 * The bill. Three acts, descending, exactly like a lineup — and every line
 * under them is a fact pulled from the product's own constants rather than
 * written for the poster.
 */
const BILL = [
  {
    act: 'The Rack',
    tier: 'headline' as const,
    support: [
      `${MOOD_LIST.length} moods, not one size`,
      'Sunlit · Maximal · Nostalgic · Feral',
      'Sorted by how you want to feel',
    ],
  },
  {
    act: 'The Studio',
    tier: 'second' as const,
    support: [
      'One photo of you',
      `Up to ${RACKSAVANT.maxGarmentsPerLook} pieces in a look`,
      'The look develops while you watch',
    ],
  },
  {
    act: 'The Fitting Room',
    tier: 'third' as const,
    support: [
      `${ROOM_CODE_LENGTH} letters into the group chat`,
      `${RACKSAVANT.roomTtlMs / 3_600_000} hours, then it closes`,
      `${HYPE_LIST.length} reactions with an opinion in them`,
    ],
  },
];

/** The festival-framed product argument. Three claims, all of them checkable. */
const ARGUMENT = [
  {
    index: '01',
    title: 'Mood, not size',
    body: 'Nobody packs for a park by looking up a measurement. They pack for how three days are going to feel — one hot and loud, one long and golden, one cold by six. The rack is indexed against that, so you start from the feeling and the clothes rearrange under it.',
  },
  {
    index: '02',
    title: 'One photo, then it is only choosing',
    body: 'You give it a single body shot, once. Everything after that is picking pieces and watching them land on you. No measurements, no body scan, no field where your size goes — we never wanted one.',
  },
  {
    index: '03',
    title: 'The look develops, and then it talks',
    body: 'A fitting comes back as an image and a read: a verdict, the adjectives it is actually giving, the colour story holding it together, and a caption you can paste. It talks about the clothes and never about your body.',
  },
];

/* ------------------------------------------------------------------ page */

export function OutsideLandsPage() {
  const [mood, setMood] = useState<MoodId>('sunlit');
  const stats = useStats();
  const garments = useGarments(mood, 12);
  const runway = useRunway(6);
  const active = MOODS[mood];

  /* The festival palette, taken straight from the mood taxonomy so it can
     never drift from the colours the product actually uses. */
  const palette = {
    '--sun': MOODS.sunlit.color,
    '--sun-alt': MOODS.sunlit.colorAlt,
    '--carnival': MOODS.maximal.color,
    '--super8': MOODS.nostalgic.color,
    '--riot': MOODS.feral.color,
    '--mood': active.color,
    '--mood-alt': active.colorAlt,
  } as CSSProperties;

  /* Only measured counts. A stat that comes back zero, or does not come back,
     is dropped — the strip renders whatever is left, or nothing at all. The
     labels carry a singular because an early product genuinely does hit 1, and
     "1 reactions sent" reads like a placeholder even when it is the truth. */
  const counts = stats
    ? [
        { value: stats.garments, one: 'piece on the rack', many: 'pieces on the rack' },
        { value: stats.moods, one: 'mood in the taxonomy', many: 'moods in the taxonomy' },
        { value: stats.publicLooks, one: 'look on the Runway', many: 'looks on the Runway' },
        { value: stats.hypeSent, one: 'reaction sent', many: 'reactions sent' },
      ].filter((count) => count.value > 0)
    : [];

  return (
    <article className={s.page} style={palette}>
      {/* ------------------------------------------------------------ Hero */}
      <section className={s.hero} aria-labelledby="ol-title">
        <div className={cx(ui.container, s.heroGrid)}>
          {/* The poster. Printed on bone, on an ink page. */}
          <div className={s.poster}>
            <div className={s.posterInner}>
              <p className={s.posterTop}>
                <span>Hackathon entry</span>
                <span aria-hidden="true">✳</span>
                <span>San Francisco</span>
                <span aria-hidden="true">✳</span>
                <span>Golden Gate Park</span>
              </p>

              <h1 id="ol-title" className={s.billing}>
                <Wipe
                  lines={[
                    <span key="a" className={s.billingA}>
                      RackSavant
                    </span>,
                    <span key="b" className={s.billingCross}>
                      ×
                    </span>,
                    <span key="c" className={s.billingB}>
                      Outside Lands
                    </span>,
                  ]}
                />
              </h1>

              <p className={s.posterLede}>
                Three days in the park. One suitcase, packed on a Thursday night, in a bedroom, in
                bad light. <em>Try the whole weekend on before you zip it.</em>
              </p>

              <ol className={s.bill}>
                {BILL.map((entry) => (
                  <li key={entry.act} className={cx(s.billRow, s[entry.tier])}>
                    <h2 className={s.billAct}>{entry.act}</h2>
                    <p className={s.billSupport}>
                      {entry.support.map((line, i) => (
                        <span key={line}>
                          {i > 0 ? (
                            <span className={s.billDot} aria-hidden="true">
                              ·
                            </span>
                          ) : null}
                          {line}
                        </span>
                      ))}
                    </p>
                  </li>
                ))}
              </ol>

              {/* The small-type run along the bottom of every lineup poster.
                  Ours is the real taxonomy, in order. */}
              <p className={s.billFoot}>
                {MOOD_LIST.map((m) => m.label).join(' · ')}
              </p>

              <div className={s.posterCtas}>
                <Link to="/auth" className={cx(s.btn, s.btnSolid)}>
                  Try on the weekend
                  <ArrowRight size={16} />
                </Link>
                <a href="#ol-pack" className={cx(s.btn, s.btnLine)}>
                  What to pack
                </a>
              </div>

              <p className={s.posterSmall}>
                An independent hackathon entry by RackSavant. Not affiliated with, endorsed by or a
                partner of the Outside Lands festival or its organisers.
              </p>
            </div>
          </div>

          {/* The collage. Plates overprinted in festival ink, cut-outs on top. */}
          <div className={s.collage}>
            <div className={cx(s.riso, s.risoSun, s.collageA)}>
              <Plate id="group" caption={false} ratio={3 / 4} focus="50% 38%" eager />
            </div>
            <div className={cx(s.riso, s.risoRiot, s.collageB)}>
              <Plate id="duo" caption={false} ratio={1} focus="50% 42%" />
            </div>
            <div className={cx(s.riso, s.risoSuper8, s.collageC)}>
              <Plate id="guyStreet" caption={false} ratio={3 / 4} />
            </div>
            <Cutout id="sunglasses" width={186} tilt={-8} className={s.collageGlasses} />
            <Cutout id="jacket" width={168} tilt={7} className={s.collageJacket} />
            <Face kind="obsessed" size={92} tilt={-6} className={s.collageFaceA} />
            <Face kind="menace" size={78} tilt={6} className={s.collageFaceB} />
            <p className={s.collageCredit}>
              Three friends, one lift, four floors. <em>Same problem, bigger park.</em>
            </p>
          </div>
        </div>
      </section>

      <Marquee duration={32000} className={s.band}>
        {[0, 1, 2, 3].map((n) => (
          <span key={n} className={s.bandItem}>
            Try on the weekend
            <span className={s.bandStar} aria-hidden="true">
              ✳
            </span>
            Three days
            <span className={s.bandStar} aria-hidden="true">
              ✳
            </span>
            One suitcase
            <span className={s.bandStar} aria-hidden="true">
              ✳
            </span>
          </span>
        ))}
      </Marquee>

      {/* ------------------------------------------------------- The pitch */}
      <section id="ol-pack" className={ui.section} aria-labelledby="ol-pack-title">
        <div className={cx(ui.container, s.pitchGrid)}>
          <Reveal className={s.pitchHead}>
            <span className={ui.crop} aria-hidden="true" />
            <p className={ui.eyebrow}>The actual question</p>
            <h2 id="ol-pack-title" className={cx(ui.h2, ui.misreg)}>
              What do you wear to three days in a <em className={s.parkWord}>park</em>?
            </h2>
            <p className={ui.lede}>
              Everyone answers it the same way: pull half the wardrobe onto the bed, try four things
              in a mirror at eleven at night, photograph two of them, send them to the group chat,
              and pack the one you already owned. RackSavant is that hour, done properly.
            </p>
            <div className={cx(s.riso, s.risoSun, s.pitchPlate)}>
              <Plate id="mirror" caption={false} ratio={4 / 5} />
            </div>
            <p className={ui.caption}>One photo, phone at chest height. That is the whole setup.</p>
          </Reveal>

          <ol className={s.argument}>
            {ARGUMENT.map((item, i) => (
              <li key={item.title}>
                <Reveal className={s.arg} delay={i * 90}>
                  <span className={s.argIndex}>{item.index}</span>
                  <h3 className={s.argTitle}>{item.title}</h3>
                  <p className={s.argBody}>{item.body}</p>
                </Reveal>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* --------------------------------------------------- Product proof */}
      <section className={s.deckSection} aria-labelledby="ol-deck-title">
        <div className={cx(ui.container, s.deckGrid)}>
          <Reveal className={s.deckHead}>
            <span className={ui.crop} aria-hidden="true" />
            <p className={ui.eyebrow}>The product, running</p>
            <h2 id="ol-deck-title" className={ui.h2}>
              Not a mockup. The <em className={ui.it}>actual</em> thing.
            </h2>
            <p className={ui.lede}>
              Six surfaces, recorded off the live app on a phone and on a desktop — the rack, the
              studio, a look developing, a finished look, a fitting room and the Runway. Drag the
              top card to deal the next one.
            </p>
          </Reveal>
          <DeviceDeck className={s.deck} />
        </div>
      </section>

      {/* ------------------------------------------ Moods + live catalogue */}
      <section id="ol-moods" className={s.moodSection} aria-labelledby="ol-moods-title">
        <div className={ui.container}>
          <Reveal className={s.moodHead}>
            <span className={ui.crop} aria-hidden="true" />
            <p className={ui.eyebrow}>Four moods for a weekend outdoors</p>
            <h2 id="ol-moods-title" className={ui.h2}>
              A festival is not one feeling. It is <em className={ui.it}>four</em>, in order.
            </h2>
            <p className={ui.lede}>
              Sun on the shoulders at two. More is the whole point by six. A summer you miss by
              nine. Something a bit dangerous by eleven. Pick one and the rack rearranges — these
              are the real pieces on it right now.
            </p>
          </Reveal>

          <div className={s.moodPicker} role="group" aria-label="Festival moods">
            {FESTIVAL_MOODS.map((id) => {
              const m = MOODS[id];
              return (
                <button
                  key={id}
                  type="button"
                  className={cx(s.moodCard, id === mood && s.moodCardOn)}
                  style={{ '--chip': m.color, '--chip-alt': m.colorAlt } as CSSProperties}
                  aria-pressed={id === mood}
                  onClick={() => setMood(id)}
                >
                  <span className={s.moodSwatch} aria-hidden="true" />
                  <span className={s.moodName}>{m.label}</span>
                  <span className={s.moodBlurb}>{m.blurb}</span>
                  <span className={s.moodLight}>{m.lightingHint}</span>
                  <span className={s.moodEnergy} aria-hidden="true">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <span key={n} className={cx(s.pip, n <= m.energy && s.pipOn)} />
                    ))}
                  </span>
                  <span className={ui.srOnly}>Energy {m.energy} of 5</span>
                </button>
              );
            })}
          </div>

          <div className={s.rackHead}>
            <h3 className={s.rackTitle} aria-live="polite">
              On the rack under <em>{active.label}</em>
            </h3>
            <p className={s.rackNote}>
              Pulled live from the catalogue. Prices and pieces are whatever the rack is holding as
              this page loads.
            </p>
          </div>

          {garments.length > 0 ? (
            <ul className={s.rack}>
              {garments.map((garment) => (
                <li key={garment.id} className={s.garment}>
                  <span className={cx(s.riso, s.risoMood, s.garmentFrame)}>
                    <img
                      className={s.garmentImg}
                      src={mediaSrc(garment.thumbUrl || garment.imageUrl)}
                      alt={`${garment.name}${garment.brand ? ` by ${garment.brand}` : ''}, ${GARMENT_CATEGORY_LABELS[garment.category].toLowerCase()}`}
                      loading="lazy"
                      decoding="async"
                    />
                  </span>
                  <p className={s.garmentName}>{garment.name}</p>
                  <p className={s.garmentMeta}>
                    {garment.brand ? <span>{garment.brand}</span> : null}
                    <span>{GARMENT_CATEGORY_LABELS[garment.category]}</span>
                    {garment.priceCents !== null ? (
                      <span>{formatPrice(garment.priceCents, garment.currency)}</span>
                    ) : null}
                  </p>
                  {garment.palette.length > 0 ? (
                    <span className={s.garmentPalette} aria-hidden="true">
                      {garment.palette.slice(0, 4).map((hex) => (
                        <span key={hex} style={{ background: hex }} />
                      ))}
                    </span>
                  ) : null}
                </li>
              ))}
            </ul>
          ) : (
            /* No stand-in rows. If the rack is not reachable the section still
               reads, because the sentence above never promised a number. */
            <p className={s.rackEmpty}>
              The rack is not answering from here. Open the studio and it will be waiting inside.
            </p>
          )}
        </div>
      </section>

      {/* ----------------------------------------------------- Real looks */}
      {runway.length > 0 ? (
        <section className={ui.section} aria-labelledby="ol-runway-title">
          <div className={ui.container}>
            <Reveal className={s.runwayHead}>
              <span className={ui.crop} aria-hidden="true" />
              <p className={cx(ui.eyebrow, s.liveEyebrow)}>
                <span className={ui.liveDot} aria-hidden="true" />
                Live from the Runway
              </p>
              <h2 id="ol-runway-title" className={ui.h2}>
                Looks other people actually made.
              </h2>
              <p className={ui.lede}>
                Public fittings from the app as it stands, with the read the model wrote for each
                one. Nothing here was staged for this page.
              </p>
            </Reveal>

            <ul className={s.looks}>
              {runway.map((look) => {
                const read = look.moodRead;
                const author = look.author?.username;
                const adjectives = read?.adjectives.slice(0, 3).join(', ');
                return (
                  <li key={look.id} className={s.look}>
                    <span className={cx(s.riso, s.risoSoft, s.lookFrame)}>
                      <img
                        className={s.lookImg}
                        src={mediaSrc(look.thumbUrl || look.url)}
                        alt={
                          adjectives
                            ? `A finished look${author ? ` by ${author}` : ''} — ${adjectives}.`
                            : `A finished look${author ? ` by ${author}` : ''}.`
                        }
                        width={look.width}
                        height={look.height}
                        loading="lazy"
                        decoding="async"
                      />
                    </span>
                    {read ? <p className={s.lookVerdict}>{read.verdict}</p> : null}
                    <p className={s.lookMeta}>
                      {look.title ? <span className={s.lookTitle}>{look.title}</span> : null}
                      {author ? <span>@{author}</span> : null}
                      {look.intendedMood ? <span>{MOODS[look.intendedMood].label}</span> : null}
                    </p>
                  </li>
                );
              })}
            </ul>
          </div>
        </section>
      ) : null}

      {/* ---------------------------------------------------- Live counts */}
      {counts.length > 0 ? (
        <section className={s.counts} aria-labelledby="ol-counts-title">
          <div className={cx(ui.container, s.countsInner)}>
            <h2 id="ol-counts-title" className={ui.srOnly}>
              Where the product stands today
            </h2>
            <dl className={s.countList}>
              {counts.map((count) => (
                <div key={count.many} className={s.count}>
                  <dt className={s.countValue}>{formatCount(count.value)}</dt>
                  <dd className={s.countLabel}>{count.value === 1 ? count.one : count.many}</dd>
                </div>
              ))}
            </dl>
            <p className={s.countNote}>
              Counted from the live API as this page loaded. Where a number is missing, it is
              because we would rather print nothing than print a guess.
            </p>
          </div>
        </section>
      ) : null}

      {/* --------------------------------------------------------- Closing */}
      <section className={s.final} aria-labelledby="ol-final-title">
        <div className={cx(s.riso, s.risoCarnival, s.finalPlate)}>
          <Plate id="hero" caption={false} ratio={16 / 9} focus="50% 34%" />
        </div>

        <div className={cx(ui.container, s.finalInner)}>
          <h2 id="ol-final-title" className={s.finalTitle}>
            <Wipe
              lines={[
                <span key="a">Pack it on Thursday.</span>,
                <span key="b">
                  Wear it in the <em>park</em>.
                </span>,
              ]}
            />
          </h2>
          <p className={s.finalSub}>
            Free to start — {RACKSAVANT.freeFittingsPerDay} fittings a day, no card, and delete
            means delete. Open a fitting room, put the code in the group chat, and settle the
            weekend before Friday.
          </p>
          <div className={s.finalCtas}>
            <Link to="/auth" className={cx(ui.btn, ui.primary)}>
              Open the studio
              <ArrowRight size={16} />
            </Link>
            <Link to="/" className={cx(ui.btn, ui.ghost)}>
              The rest of RackSavant
            </Link>
          </div>

          <div className={s.colophon}>
            <h3 className={s.colophonTitle}>What this is</h3>
            <p>
              RackSavant is a real, working product: a try-on studio, a mood-indexed rack and live
              fitting rooms. This page is our entry in a hackathon held around the Outside Lands
              festival in Golden Gate Park, San Francisco. It was built by us, in our own type and
              our own colour, using the festival&rsquo;s own format — a lineup bill — as its layout.
            </p>
            <p>
              We are not affiliated with the festival, we are not sponsored by it, and no part of
              its branding, artwork or lineup appears here. The name is used descriptively, to say
              what the entry was made for. Every photograph on this page is our own editorial
              shoot, and every count is read from our own API.
            </p>
            <Cutout id="bag" width={112} tilt={-6} className={s.colophonCutout} />
          </div>
        </div>
      </section>
    </article>
  );
}
